import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import usePageTitle from 'hooks/usePageTitle';
import useEscapeClose from 'hooks/useEscapeClose';

const TYPES_RDV = [
  { value: 'reunion', label: 'Reunion' },
  { value: 'rdv', label: 'Rendez-vous' },
  { value: 'deplacement', label: 'Deplacement' },
  { value: 'rappel', label: 'Rappel' },
];

const STATUTS = [
  { value: 'planifie', label: 'Planifie' },
  { value: 'confirme', label: 'Confirme' },
  { value: 'annule', label: 'Annule' },
  { value: 'termine', label: 'Termine' },
];

const EMPTY = { titre: '', description: '', date_debut: '', date_fin: '', lieu: '', type_rdv: 'reunion', participants: '', rappel_avant_min: 30, notes: '' };

export default function AgendaDG() {
  usePageTitle('Agenda DG');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({ statut: '', type_rdv: '', search: '' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [listRes, statsRes] = await Promise.all([
        api.get('/assistanat/agenda-dg', { params }),
        api.get('/assistanat/agenda-dg/stats'),
      ]);
      setItems(listRes.data);
      setStats(statsRes.data);
    } catch {} finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titre || !form.date_debut) return alert('Titre et date de debut requis');
    setSaving(true);
    try {
      const payload = {
        ...form,
        participants: form.participants ? form.participants.split(',').map(p => p.trim()).filter(Boolean) : [],
        rappel_avant_min: parseInt(form.rappel_avant_min) || 30,
      };
      if (editId) {
        await api.put(`/assistanat/agenda-dg/${editId}`, payload);
      } else {
        await api.post('/assistanat/agenda-dg', payload);
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setForm({
      titre: item.titre || '', description: item.description || '',
      date_debut: item.date_debut ? item.date_debut.slice(0, 16) : '',
      date_fin: item.date_fin ? item.date_fin.slice(0, 16) : '',
      lieu: item.lieu || '', type_rdv: item.type_rdv || 'reunion',
      participants: (item.participants || []).join(', '),
      rappel_avant_min: item.rappel_avant_min || 30,
      notes: item.notes || '', statut: item.statut,
    });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return;
    try {
      await api.delete(`/assistanat/agenda-dg/${id}`);
      load();
    } catch {}
  };

  const updateStatut = async (id, statut) => {
    try {
      await api.put(`/assistanat/agenda-dg/${id}`, { statut });
      load();
    } catch {}
  };

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const setFld = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const typeColor = (t) => {
    const colors = { reunion: { bg: '#E3F2FD', color: '#1565C0' }, rdv: { bg: '#F3E5F5', color: '#7B1FA2' },
      deplacement: { bg: '#FFF3E0', color: '#F57C00' }, rappel: { bg: '#E8F5E9', color: '#2E7D32' } };
    return colors[t] || { bg: '#F5F5F5', color: '#616161' };
  };

  const statutBadge = (s) => {
    const map = { planifie: '', confirme: 'gss-badge-green', annule: '', termine: 'gss-badge-grey' };
    const styles = { planifie: { background: '#E3F2FD', color: '#1565C0' }, annule: { background: '#FEE2E2', color: '#DC2626' } };
    return { cls: map[s] || '', style: styles[s] || {} };
  };

  const formatDateTime = (dt) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const typeLabel = (t) => TYPES_RDV.find(x => x.value === t)?.label || t;

  return (
    <div>
<div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Total</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div></div>
          <div className="stat-card-value">{stats.total || 0}</div>
          <div className="stat-card-sub">evenements</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Planifies</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div></div>
          <div className="stat-card-value" style={{ color: '#1565C0' }}>{stats.planifie || 0}</div>
          <div className="stat-card-sub">a venir</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Confirmes</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-success)' }}>{stats.confirme || 0}</div>
          <div className="stat-card-sub">valides</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Aujourd'hui</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-warning)' }}>{stats.aujourd_hui || 0}</div>
          <div className="stat-card-sub">evenements</div>
        </div>
      </div>

      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="form-input" placeholder="Rechercher..." value={filters.search}
              onChange={e => setF('search', e.target.value)} style={{ maxWidth: 200 }} />
            <select className="form-input" value={filters.type_rdv} onChange={e => setF('type_rdv', e.target.value)} style={{ maxWidth: 160 }}>
              <option value="">Tous types</option>
              {TYPES_RDV.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select className="form-input" value={filters.statut} onChange={e => setF('statut', e.target.value)} style={{ maxWidth: 150 }}>
              <option value="">Tous statuts</option>
              {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <button className="gss-btn gss-btn-primary" onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(true); }}>
            + Nouveau RDV
          </button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Type</th>
                  <th>Date debut</th>
                  <th>Date fin</th>
                  <th>Lieu</th>
                  <th>Participants</th>
                  <th>Statut</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Aucun evenement</td></tr>
                )}
                {items.map(item => {
                  const tc = typeColor(item.type_rdv);
                  const sb = statutBadge(item.statut);
                  return (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.titre}</strong>
                        {item.description && <div style={{ fontSize: '0.75rem', color: 'var(--gss-text-light)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description}</div>}
                      </td>
                      <td><span className="gss-badge" style={{ background: tc.bg, color: tc.color }}>{typeLabel(item.type_rdv)}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(item.date_debut)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(item.date_fin)}</td>
                      <td>{item.lieu || '-'}</td>
                      <td>
                        {item.participants && item.participants.length > 0
                          ? item.participants.slice(0, 2).join(', ') + (item.participants.length > 2 ? ` +${item.participants.length - 2}` : '')
                          : '-'}
                      </td>
                      <td><span className={`gss-badge ${sb.cls}`} style={sb.style}>{item.statut}</span></td>
                      <td>
                        <div className="gss-actions">
                          <button className="gss-btn-icon" onClick={() => handleEdit(item)} title="Modifier">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          {item.statut === 'planifie' && (
                            <button className="gss-btn-sm" style={{ background: '#E8F5E9', color: '#2E7D32' }} onClick={() => updateStatut(item.id, 'confirme')}>Confirmer</button>
                          )}
                          {(item.statut === 'planifie' || item.statut === 'confirme') && (
                            <button className="gss-btn-sm" style={{ background: '#FEE2E2', color: '#DC2626' }} onClick={() => updateStatut(item.id, 'annule')}>Annuler</button>
                          )}
                          {item.statut === 'confirme' && (
                            <button className="gss-btn-sm" style={{ background: '#F5F5F5', color: '#616161' }} onClick={() => updateStatut(item.id, 'termine')}>Terminer</button>
                          )}
                          <button className="gss-btn-icon gss-btn-icon--delete" onClick={() => handleDelete(item.id)} title="Archiver">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* FORMULAIRE MODAL */}
      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <h2>{editId ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}</h2>
                <div className="gss-modal-subtitle">Agenda de la Direction Generale</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                {/* Section: Informations */}
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  <span>INFORMATIONS DU RENDEZ-VOUS</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Titre *</label>
                    <input className="form-input" value={form.titre} onChange={e => setFld('titre', e.target.value)} required />
                  </div>
                  <div className="gss-field">
                    <label className="form-label">Type</label>
                    <select className="form-input" value={form.type_rdv} onChange={e => setFld('type_rdv', e.target.value)}>
                      {TYPES_RDV.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="gss-field">
                    <label className="form-label">Lieu</label>
                    <input className="form-input" value={form.lieu} onChange={e => setFld('lieu', e.target.value)} />
                  </div>
                </div>

                {/* Section: Planification */}
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <span>PLANIFICATION</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label className="form-label">Date et heure debut *</label>
                    <input className="form-input" type="datetime-local" value={form.date_debut} onChange={e => setFld('date_debut', e.target.value)} required />
                  </div>
                  <div className="gss-field">
                    <label className="form-label">Date et heure fin</label>
                    <input className="form-input" type="datetime-local" value={form.date_fin} onChange={e => setFld('date_fin', e.target.value)} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Participants (separes par des virgules)</label>
                    <input className="form-input" value={form.participants} onChange={e => setFld('participants', e.target.value)}
                      placeholder="Ex: M. Dupont, Mme Martin" />
                  </div>
                  <div className="gss-field">
                    <label className="form-label">Rappel (minutes avant)</label>
                    <input className="form-input" type="number" value={form.rappel_avant_min} onChange={e => setFld('rappel_avant_min', e.target.value)} />
                  </div>
                  {editId && (
                    <div className="gss-field">
                      <label className="form-label">Statut</label>
                      <select className="form-input" value={form.statut} onChange={e => setFld('statut', e.target.value)}>
                        {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {/* Section: Details */}
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/>
                  </svg>
                  <span>DETAILS COMPLEMENTAIRES</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Description</label>
                    <textarea className="form-input" rows="3" value={form.description} onChange={e => setFld('description', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Notes</label>
                    <textarea className="form-input" rows="2" value={form.notes} onChange={e => setFld('notes', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg> Enregistrer</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
